import { Fragment, useEffect, useMemo, useState, type ClipboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  alterarVerticais, MOTIVOS_DE_PARADA,
  type CampanhaCompleta, type MotivoDeParada, type VerticalDaCampanha,
} from '../../api/cptu';
import { ErroApi } from '../../api';
import { plural } from '../../formato';
import { Area, Dialogo, ErroDaApi } from '../../ui';
import { EscolhaDoCone } from './CampanhaVerticais';
import {
  avisoDoCone, blocoColado, colunasDoModo, comCoordenadaGravada, coordenadaMudou, distribuir, errosDaLinha, FUSOS,
  mudancasDa, NOME_DA_COLUNA, outroFuso, rascunhoDe, textoDoCone,
  type ColunaDeTexto, type ModoDaCoordenada, type RascunhoDaVertical, type SistemaDaCoordenada,
} from './edicaoRapida';
import { FormularioDaJanela } from './FormularioDaJanela';
import { useFocoDeVolta } from './interpretacao';

/**
 * A Edição rápida das verticais — onda 333, no molde da Edição rápida das
 * amostras da OS (onda 276): a tabela vira grade, cada vertical ativa com o
 * NA, a cota, a latitude, a longitude, o cone e o encerramento; o que mudou
 * fica marcado na linha; o bloco copiado do Excel se distribui pelas linhas
 * seguintes e pelas colunas à direita; e «Gravar as N verticais» pede o motivo
 * UMA vez e grava o lote numa transação (`PATCH /cptu/campanhas/:id/verticais`,
 * todas ou nenhuma). O NA, a cota e a coordenada vão também ao ponto da OS.
 *
 * A coordenada (onda 334, «igual as amostras de Laboratório»): a faixa acima
 * da grade escolhe o sistema — UTM (SIRGAS 2000) com o fuso, ou geográficas —
 * para a grade inteira, como na Edição rápida das amostras; o banco converte o
 * UTM. Enquanto há coordenada digitada e não gravada, o sistema não troca (a
 * troca reescreveria as células no outro sistema).
 *
 * Os rascunhos (e o sistema) moram em quem chama (`CampanhaVerticais`): passar pelos 640 px
 * esconde a grade sem perder o que foi digitado. Eles casam com as verticais
 * pelo id (revisão da 333), não pela posição: a vertical que some no
 * recarregamento leva o rascunho junto, e a que entra vem com os valores
 * gravados.
 */
export function EdicaoRapidaDasVerticais({
  c, verticais, rascunhos, aoMudarRascunhos, sistema, aoMudarSistema, aoSair, aoGravado,
}: {
  c: CampanhaCompleta;
  verticais: VerticalDaCampanha[];
  rascunhos: Record<string, RascunhoDaVertical>;
  aoMudarRascunhos(r: Record<string, RascunhoDaVertical>): void;
  sistema: SistemaDaCoordenada;
  aoMudarSistema(s: SistemaDaCoordenada): void;
  aoSair(): void;
  aoGravado(n: number): Promise<void>;
}) {
  const navegar = useNavigate();
  const [janela, setJanela] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [errosDoServidor, setErrosDoServidor] = useState<Record<string, string>>({});
  const [ocupado, setOcupado] = useState(false);
  const [colagem, setColagem] = useState<string | null>(null);
  /** Para onde a pessoa quis ir com mudança na grade (`'sair'` = o botão «Sair da edição»). */
  const [saida, setSaida] = useState<string | null>(null);
  useFocoDeVolta(janela);
  useFocoDeVolta(saida !== null);

  /** Os rascunhos na ordem das verticais de agora: a que não tem rascunho entra com os valores gravados. */
  const atuais = useMemo(() => verticais.map((v) => rascunhos[v.id] ?? rascunhoDe(v, sistema.fuso)),
    [verticais, rascunhos, sistema.fuso]);
  const colunas = colunasDoModo(sistema.modo);
  /** De volta ao registro por id — só as verticais de agora (a que sumiu sai). */
  const porId = (lista: RascunhoDaVertical[]) => Object.fromEntries(verticais.map((v, i) => [v.id, lista[i]]));
  const limparErrosDoServidor = (ids: string[]) => {
    if (!ids.some((id) => errosDoServidor[id])) return;
    setErrosDoServidor((antes) => Object.fromEntries(Object.entries(antes).filter(([id]) => !ids.includes(id))));
  };

  const linhas = useMemo(() => verticais.map((v, i) => {
    const r = atuais[i];
    const erros = errosDaLinha(r, sistema.modo);
    const temErro = Object.keys(erros).length > 0;
    const mudancas = temErro ? null : mudancasDa(v, r, sistema);
    return { v, r, erros, temErro, mudancas, sujo: temErro || !!mudancas };
  }), [verticais, atuais, sistema]);
  /** Coordenada digitada e não gravada: o sistema e o fuso ficam como estão até gravar ou desfazer. */
  const coordenadaPendente = linhas.filter((l) => coordenadaMudou(l.v, l.r, sistema)).length;
  const trocarSistema = (novo: SistemaDaCoordenada) => {
    if (coordenadaPendente) return;
    aoMudarSistema(novo);
    aoMudarRascunhos(porId(atuais.map((r, i) => comCoordenadaGravada(verticais[i], r, novo.fuso))));
  };
  const comErro = linhas.filter((l) => l.temErro).length;
  const mudadas = linhas.filter((l) => l.mudancas);
  const sujo = linhas.some((l) => l.sujo);

  /* Sair da aba com mudança na grade pergunta (o molde da aba Premissas da 313): o clique num link interno é
     segurado antes do roteador; o F5 e o fechar da aba ficam com o aviso do navegador. */
  useEffect(() => {
    if (!sujo) return undefined;
    const aoClicar = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
      if (!(a instanceof HTMLAnchorElement) || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      setSaida(`${url.pathname}${url.search}${url.hash}`);
    };
    const aoSairDaPagina = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('click', aoClicar, true);
    window.addEventListener('beforeunload', aoSairDaPagina);
    return () => {
      window.removeEventListener('click', aoClicar, true);
      window.removeEventListener('beforeunload', aoSairDaPagina);
    };
  }, [sujo]);

  const mudar = (i: number, parte: Partial<RascunhoDaVertical>) => {
    aoMudarRascunhos(porId(atuais.map((r, j) => (j === i ? { ...r, ...parte } : r))));
    limparErrosDoServidor([verticais[i].id]);
  };

  /** O bloco do Excel: distribuído a partir da célula; a célula só cola como sempre. */
  const aoColar = (i: number, col: ColunaDeTexto) => (e: ClipboardEvent<HTMLInputElement>) => {
    const bloco = blocoColado(e.clipboardData.getData('text/plain'));
    if (!bloco) return;
    e.preventDefault();
    const r = distribuir(atuais, i, col, bloco, colunas);
    aoMudarRascunhos(porId(r.rascunhos));
    /* Como o `mudar`: o erro do servidor some das linhas em que a colagem escreveu. */
    limparErrosDoServidor(verticais.slice(i, i + bloco.length).map((v) => v.id));
    const fora = [
      r.linhasDeFora ? plural(r.linhasDeFora, 'linha passou da última vertical', 'linhas passaram da última vertical') : null,
      r.celulasDeFora ? plural(r.celulasDeFora, `célula passou da coluna ${NOME_DA_COLUNA[colunas[colunas.length - 1]]}`,
        `células passaram da coluna ${NOME_DA_COLUNA[colunas[colunas.length - 1]]}`) : null,
    ].filter(Boolean).join('; ');
    setColagem(`Colado da planilha: ${plural(bloco.length, 'linha', 'linhas')} a partir de ${verticais[i].codigo}, `
      + `${col === 'e' || col === 'n' ? NOME_DA_COLUNA[col] : NOME_DA_COLUNA[col].toLowerCase()}.${fora ? ` Ficou de fora: ${fora}.` : ''}`);
  };

  const motivoDoGravar = comErro
    ? `Corrija ${plural(comErro, 'vertical', 'verticais')} antes de gravar.`
    : mudadas.length === 0 ? 'Nada mudou ainda.' : null;
  const rotuloDoGravar = mudadas.length === 1 ? 'Gravar 1 vertical'
    : mudadas.length ? `Gravar as ${mudadas.length} verticais` : 'Gravar';
  const curto = motivo.trim().length < 5;

  const gravar = async () => {
    if (motivoDoGravar || curto || ocupado) return;
    setOcupado(true); setErro(null);
    const lote = mudadas.map((l) => l.mudancas!.corpo);
    try {
      await alterarVerticais(c.campanha.id, lote, motivo.trim());
      setJanela(false);
      setMotivo('');
      setOcupado(false);
      await aoGravado(lote.length);
    } catch (e) {
      setOcupado(false);
      /* O erro de uma vertical vem no caminho `verticais.<i>`: vai à linha dela. */
      const porLinha: Record<string, string> = {};
      if (e instanceof ErroApi) {
        for (const x of e.campos) {
          const m = /^verticais\.(\d+)(?:\.|$)/.exec(x.campo);
          const id = m ? lote[Number(m[1])]?.id : undefined;
          if (id) porLinha[id] = porLinha[id] ? `${porLinha[id]}; ${x.erro}` : x.erro;
        }
      }
      setErrosDoServidor(porLinha);
      if (Object.keys(porLinha).length) setJanela(false);
      setErro(e);
    }
  };

  const sair = () => (sujo ? setSaida('sair') : aoSair());

  return (
    <section className="cartao cptu-er" aria-label="Edição rápida das verticais">
      <div className="cptu-er-barra">
        <p className="cptu-er-dica">
          {plural(verticais.length, 'vertical ativa', 'verticais ativas')} · {sistema.modo === 'utm'
            ? `E e N em metros, UTM fuso ${sistema.fuso}S (SIRGAS 2000)`
            : 'latitude e longitude em graus decimais (sul e oeste negativos, ou com S e O/W)'} · cole um bloco da
          planilha numa célula e ele se distribui pelas linhas de baixo e pelas colunas à direita.
        </p>
        <div className="cptu-er-acoes">
          <button type="button" className="botao secundario" onClick={sair}>Sair da edição</button>
          <button type="button" className="botao" aria-disabled={motivoDoGravar ? true : undefined}
            aria-describedby={motivoDoGravar ? 'cptu-er-motivo-gravar' : undefined}
            onClick={() => { if (!motivoDoGravar) { setErro(null); setJanela(true); } }}>
            {rotuloDoGravar}
          </button>
          {motivoDoGravar && <small id="cptu-er-motivo-gravar" className="cptu-motivo-bloqueio">{motivoDoGravar}</small>}
        </div>
      </div>
      {/*
        * Onda 334: a faixa das coordenadas vale para a grade inteira, como na Edição rápida das amostras —
        * um seletor por linha seria a mesma escolha repetida, e uma chance de errar por linha.
        */}
      <div className="cptu-er-sistema" role="group" aria-label="Sistema das coordenadas">
        <label className="campo campo-embutido">
          <span className="rotulo">Coordenadas</span>
          <select value={sistema.modo} disabled={coordenadaPendente > 0}
            aria-describedby={coordenadaPendente ? 'cptu-er-sistema-motivo' : undefined}
            onChange={(e) => trocarSistema({ ...sistema, modo: e.target.value as ModoDaCoordenada })}>
            <option value="utm">UTM (SIRGAS 2000)</option>
            <option value="graus">Geográficas (graus decimais)</option>
          </select>
        </label>
        {sistema.modo === 'utm' && (
          <label className="campo campo-embutido">
            <span className="rotulo">Fuso</span>
            <select value={sistema.fuso} disabled={coordenadaPendente > 0}
              aria-describedby={coordenadaPendente ? 'cptu-er-sistema-motivo' : undefined}
              onChange={(e) => trocarSistema({ ...sistema, fuso: Number(e.target.value) })}>
              {FUSOS.map((f) => <option key={f} value={f}>{f}S</option>)}
            </select>
          </label>
        )}
        {coordenadaPendente > 0 && (
          <small id="cptu-er-sistema-motivo" className="cptu-motivo-bloqueio">
            {`Há coordenada digitada em ${plural(coordenadaPendente, 'vertical', 'verticais')}: grave ou desfaça antes de trocar o sistema.`}
          </small>
        )}
      </div>
      {colagem && <p className="cptu-er-colagem" role="status">{colagem}</p>}
      {erro && !janela ? <ErroDaApi erro={erro} /> : null}
      <div className="rola">
        <table className="tf-tabela cptu-er-grade" aria-label="Edição rápida das verticais">
          <thead>
            <tr>
              <th scope="col">Vertical</th>
              {colunas.map((k) => <th key={k} scope="col">{NOME_DA_COLUNA[k]}</th>)}
              <th scope="col">Cone</th>
              <th scope="col">Encerramento</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(({ v, r, erros, mudancas, temErro }, i) => {
              const doServidor = errosDoServidor[v.id];
              return (
                <Fragment key={v.id}>
                  <tr data-chave={v.id}
                    className={['cptu-er-linha', mudancas ? 'cptu-er-mudou' : '', temErro || doServidor ? 'cptu-er-com-erro' : '']
                      .filter(Boolean).join(' ')}>
                    <th scope="row">
                      <span className="mono cptu-codigo">{v.codigo}</span>
                      {mudancas && <span className="selo atencao cptu-er-selo">Alterada</span>}
                      {temErro && <span className="selo critico cptu-er-selo">Com erro</span>}
                    </th>
                    {colunas.map((k) => {
                      /* Onda 334: a coordenada gravada noutro fuso não cabe nas células deste — a célula diz qual. */
                      const fusoDela = (k === 'e' || k === 'n') ? outroFuso(v, sistema.fuso) : null;
                      const exemplo = k === 'na' ? 'não informado' : k === 'lat' ? '−27,3498' : k === 'lon' ? '−48,6384'
                        : fusoDela ? `no fuso ${fusoDela}S` : k === 'e' ? '694.123,45' : k === 'n' ? '6.968.000,00' : '—';
                      return (
                        <td key={k} className={mudancas?.campos.has(k) ? 'cptu-er-celula mudou' : 'cptu-er-celula'}>
                          <input type="text" inputMode={k === 'lat' || k === 'lon' ? 'text' : 'decimal'}
                            className="mono" value={r[k]} data-coluna={k}
                            aria-label={`${NOME_DA_COLUNA[k]} da vertical ${v.codigo}`}
                            aria-invalid={erros[k] ? true : undefined}
                            aria-describedby={erros[k] ? `cptu-er-${v.id}-${k}` : fusoDela ? `cptu-er-${v.id}-fuso` : undefined}
                            placeholder={exemplo}
                            onChange={(e) => mudar(i, { [k]: e.target.value })}
                            onPaste={aoColar(i, k)} />
                          {erros[k] && <span id={`cptu-er-${v.id}-${k}`} className="erro">{erros[k]}</span>}
                          {!erros[k] && fusoDela && k === 'e' && !r.e && !r.n && (
                            <span id={`cptu-er-${v.id}-fuso`} className="dica">
                              A coordenada gravada está no fuso {fusoDela}S: escolha {fusoDela}S para vê-la aqui.
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className={mudancas?.campos.has('cone') ? 'cptu-er-celula mudou' : 'cptu-er-celula'}>
                      <EscolhaDoCone valor={r.cone.id} rotuloAtual={r.cone.rotulo} sugestao={v.cone_codigo}
                        achado={v.cone_pelo_numero ? textoDoCone(v) : null} aviso={avisoDoCone(v)}
                        aoEscolher={(id, rotulo) => mudar(i, { cone: { id, rotulo } })} />
                    </td>
                    <td className={mudancas?.campos.has('parada') ? 'cptu-er-celula mudou' : 'cptu-er-celula'}>
                      <select value={r.parada} aria-label={`Encerramento da vertical ${v.codigo}`}
                        onChange={(e) => mudar(i, { parada: e.target.value as MotivoDeParada | '' })}>
                        <option value="">Não informado</option>
                        {MOTIVOS_DE_PARADA.map(([m, t]) => <option key={m} value={m}>{t}</option>)}
                      </select>
                      {r.parada === 'outro' && (
                        <input type="text" value={r.paradaTexto} maxLength={500}
                          aria-label={`Qual motivo do encerramento da vertical ${v.codigo}`}
                          aria-invalid={erros.paradaTexto ? true : undefined}
                          placeholder="Qual motivo"
                          onChange={(e) => mudar(i, { paradaTexto: e.target.value })} />
                      )}
                      {erros.paradaTexto && <span className="erro">{erros.paradaTexto}</span>}
                    </td>
                  </tr>
                  {doServidor && (
                    <tr className="cptu-er-erro-do-servidor">
                      <td colSpan={7}><span className="erro" role="alert">{v.codigo}: {doServidor}</span></td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {janela && (
        <Dialogo aberto titulo="Gravar a Edição rápida" rotulo={`Verticais da campanha CPTU-${String(c.campanha.sequencia).padStart(2, '0')}`}
          aoFechar={() => setJanela(false)}>
          <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
            <ErroDaApi erro={erro} />
            <p className="sub">
              {plural(mudadas.length, 'vertical muda', 'verticais mudam')}, todas ou nenhuma. As leituras e o arquivo do
              equipamento não mudam; o NA, a cota e a coordenada vão também aos pontos da OS.
            </p>
            <ul className="cptu-mudancas cptu-er-lista">
              {mudadas.map((l) => (
                <li key={l.v.id}><b className="mono">{l.v.codigo}</b>: {l.mudancas!.frases.join('; ')}</li>
              ))}
            </ul>
            <Area rotulo="Motivo da alteração" rows={2} value={motivo} maxLength={2000}
              dica="Vai para a trilha de cada vertical." onChange={(e) => setMotivo(e.target.value)} />
            {curto && <p className="cptu-falta" role="status">Diga o motivo da alteração (pelo menos 5 caracteres).</p>}
            <div className="rodape-dialogo">
              <button type="submit" className="botao" disabled={curto || ocupado}>
                {ocupado ? 'Gravando…' : rotuloDoGravar}
              </button>
              <button type="button" className="botao secundario" onClick={() => setJanela(false)}>Cancelar</button>
            </div>
          </FormularioDaJanela>
        </Dialogo>
      )}

      {saida && (
        <Dialogo aberto titulo="Sair sem gravar?" rotulo={`Campanha CPTU-${String(c.campanha.sequencia).padStart(2, '0')}`}
          aoFechar={() => setSaida(null)}>
          <p className="sub">
            {mudadas.length || comErro
              ? `O que foi digitado na Edição rápida se perde: ${plural(mudadas.length + comErro, 'vertical', 'verticais')} com mudança.`
              : 'O que foi digitado na Edição rápida se perde.'}
          </p>
          <div className="rodape-dialogo">
            <button type="button" className="botao perigo" onClick={() => {
              const para = saida;
              setSaida(null);
              aoSair();
              if (para !== 'sair') navegar(para);
            }}>Sair sem gravar</button>
            <button type="button" className="botao secundario" onClick={() => setSaida(null)}>Cancelar</button>
          </div>
        </Dialogo>
      )}
    </section>
  );
}
